import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { serviceRecordsApi } from '@/api/service-records'
import { serviceTypesApi } from '@/api/service-types'
import type {
  CreateServiceRecordPayload,
  ServiceRecordFilter,
  UpdateServiceRecordPayload,
} from '@/types/service-record'

export const serviceRecordKeys = {
  all: ['service-records'] as const,
  lists: () => [...serviceRecordKeys.all, 'list'] as const,
  list: (params?: { page?: number; page_size?: number } & ServiceRecordFilter) =>
    [...serviceRecordKeys.lists(), params] as const,
  detail: (id: number) => [...serviceRecordKeys.all, 'detail', id] as const,
  statistics: (params?: { start_date?: string; end_date?: string }) =>
    [...serviceRecordKeys.all, 'statistics', params] as const,
  statisticsPrefix: () => [...serviceRecordKeys.all, 'statistics'] as const,
  departmentStatistics: (params?: { start_date?: string; end_date?: string; scope?: string; department_id?: number }) =>
    [...serviceRecordKeys.all, 'department-statistics', params] as const,
  departmentStatisticsPrefix: () => [...serviceRecordKeys.all, 'department-statistics'] as const,
  summaryPrefix: () => [...serviceRecordKeys.all, 'summary'] as const,
}

export const serviceTypeKeys = {
  all: ['service-types'] as const,
  list: (params?: { keyword?: string; page?: number; page_size?: number }) =>
    [...serviceTypeKeys.all, 'list', params] as const,
  options: () => [...serviceTypeKeys.all, 'options'] as const,
}

export function useServiceRecords(
  params?: { page?: number; page_size?: number } & ServiceRecordFilter,
  enabled = true,
) {
  return useQuery({
    queryKey: serviceRecordKeys.list(params),
    queryFn: () => serviceRecordsApi.list(params),
    enabled,
  })
}

export function useServiceTypeOptions() {
  return useQuery({
    queryKey: serviceTypeKeys.options(),
    queryFn: () => serviceTypesApi.options(),
  })
}

export function useServiceRecordStatistics(params?: {
  start_date?: string
  end_date?: string
  scope?: string
  department_id?: number
}, enabled = true) {
  return useQuery({
    queryKey: serviceRecordKeys.statistics(params),
    queryFn: () => serviceRecordsApi.statistics(params),
    enabled,
  })
}

export function useDepartmentServiceRecordStatistics(params?: {
  start_date?: string
  end_date?: string
  scope?: string
  department_id?: number
}) {
  return useQuery({
    queryKey: serviceRecordKeys.departmentStatistics(params),
    queryFn: () => serviceRecordsApi.departmentStatistics(params),
  })
}

function useInvalidateServiceRecords() {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: serviceRecordKeys.lists() })
    queryClient.invalidateQueries({ queryKey: serviceRecordKeys.statisticsPrefix() })
    queryClient.invalidateQueries({ queryKey: serviceRecordKeys.summaryPrefix() })
  }
}

export function useCreateServiceRecord() {
  const invalidate = useInvalidateServiceRecords()
  return useMutation({
    mutationFn: (data: CreateServiceRecordPayload) => serviceRecordsApi.create(data),
    onSuccess: () => {
      invalidate()
      toast.success('服务记录已录入')
    },
  })
}

export function useUpdateServiceRecord() {
  const invalidate = useInvalidateServiceRecords()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateServiceRecordPayload }) =>
      serviceRecordsApi.update(id, data),
    onSuccess: () => {
      invalidate()
      toast.success('服务记录已更新')
    },
  })
}

export function useDeleteServiceRecord() {
  const invalidate = useInvalidateServiceRecords()
  return useMutation({
    mutationFn: (id: number) => serviceRecordsApi.delete(id),
    onSuccess: () => {
      invalidate()
      toast.success('服务记录已删除')
    },
  })
}
